import {
  ArrowDownRight,
  ArrowUpRight,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Card from "../card";
import * as S from "../dashboard/_shared";
import { compareJourneyStages } from "../../constants/journeyStages";
import { normalizeKey } from "../../utils/normalizeString";
import { formatMeta, metaDefinida } from "../../utils/meta";
import Section from "../section";
import Tag from "../tag";
import {
  cidadesDaSerie,
  dominioNpsPorCidade,
  resumoNpsPorCidade,
  transformarMetricsEmEtapaHist,
  transformarNpsPorCidadeParaGrafico,
} from "./dataTransforms";
import "./style.scss";
import { useChartAnimation } from "../charts/printMode";

export default function TrendsView({ ctx }) {
  const animarGraficos = useChartAnimation();
  const { C, F, JCFG, JICONS, TT, Z_CX, Zonas, etSt } = S;
  const AGRUP_LABELS = {
    mes: "Mensal",
    tri: "Trimestral",
    sem: "Semestral",
    ano: "Anual",
  };
  const { agrupT, setAgrupT } = ctx;

  // "Agrupar por" (mes/tri/sem/ano) é um seletor LOCAL desta aba, independente
  // do filtro de período global — mapeia pro mesmo tipo usado pelas tabelas
  // de period (monthly/quarterly/semiannual/annual).
  const AGRUP_TO_TYPE = {
    mes: "monthly",
    tri: "quarterly",
    sem: "semiannual",
    ano: "annual",
  };
  const AGRUP_SINGULAR = {
    mes: "mês",
    tri: "trimestre",
    sem: "semestre",
    ano: "ano",
  };
  const AGRUP_PLURAL = {
    mes: "meses",
    tri: "trimestres",
    sem: "semestres",
    ano: "anos",
  };
  const activePeriodType = AGRUP_TO_TYPE[agrupT];

  const overviewState = useSelector((state) => state.dashboard.overview);
  const overview = overviewState?.data?.data || [];
  // null quando o cliente não cadastrou meta: a tela mostra "-" e não
  // desenha a linha de meta.
  const metas = {
    nps: metaDefinida(overview[0]?.nps_target),
    csat: metaDefinida(overview[0]?.csat_target),
    ces: metaDefinida(overview[0]?.ces_target),
    jcx: metaDefinida(overview[0]?.cx_target),
  };

  // Meta real por etapa (state.dashboard.journey, mesma fonte que
  // tabActionPlanView usa) — não dá pra confiar em JCFG[etapa].meta aqui:
  // esse valor é hardcoded (mock) até a aba Plano de Ação montar e
  // sincronizar o valor real via mutação direta do objeto JCFG (efeito
  // colateral entre abas). Lendo journey direto, a meta já vem correta
  // desde o primeiro carregamento do dashboard, sem depender de o usuário
  // ter visitado a outra aba antes.
  const journeyState = useSelector((state) => state.dashboard.journey);
  const metaByEtapa = useMemo(() => {
    const rows = journeyState?.data?.data;
    if (!Array.isArray(rows)) return {};
    return rows.reduce((acc, e) => {
      if (e.name) acc[e.name] = metaDefinida(e.target);
      return acc;
    }, {});
  }, [journeyState]);

  // overviewHistory e segmentationHistory vêm com todos os tipos de período
  // juntos (mensal, trimestral, semestral, anual) — sem filtrar por tipo, os
  // gráficos desta aba misturam pontos de granularidades diferentes no mesmo
  // eixo X, independente do "Agrupar por" selecionado. Cruza period_name com
  // state.dashboard.periods (mesma fonte do PeriodPillBig global, que já tem
  // o campo .type) pra manter só o tipo ativo.
  const periodsState = useSelector((state) => state.dashboard.periods);
  const periodMetaByName = useMemo(() => {
    const rows = periodsState?.data?.data;
    if (!Array.isArray(rows)) return {};
    return rows.reduce((acc, p) => {
      if (p.name && !acc[p.name]) {
        acc[p.name] = { type: p.type, start_date: p.start_date };
      }
      return acc;
    }, {});
  }, [periodsState]);
  const periodTypeByName = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(periodMetaByName).map(([name, meta]) => [
          name,
          meta.type,
        ]),
      ),
    [periodMetaByName],
  );
  // /metrics só tem fk_id_period (sem a relação .period embutida), então o
  // cruzamento de tipo/granularidade aqui é por id, não por nome.
  const periodMetaById = useMemo(() => {
    const rows = periodsState?.data?.data;
    if (!Array.isArray(rows)) return {};
    return rows.reduce((acc, p) => {
      if (p.id != null) acc[p.id] = { name: p.name, type: p.type };
      return acc;
    }, {});
  }, [periodsState]);

  const segmentationState = useSelector(
    (state) => state.dashboard.segmentationHistory,
  );
  const segmentationData = (segmentationState?.data?.data || []).filter(
    (item) => periodTypeByName[item.period_name] === activePeriodType,
  );
  // Score histórico por etapa vem direto de /metrics — não de
  // /drivers-metrics. Esse último é o join etapa↔causa qualitativa: uma
  // etapa só aparecia nele se algum driver tivesse sido taggeado naquele
  // período, então etapas sem causa extraída sumiam da grade de cards
  // inteiras mesmo com o score normal. /metrics tem o score sem essa
  // dependência.
  const metricsHistoryState = useSelector(
    (state) => state.dashboard.metricsHistory,
  );
  const metricsHistoryData = (metricsHistoryState?.data?.data || []).filter(
    (item) => periodMetaById[item.fk_id_period]?.type === activePeriodType,
  );
  const overviewHistoryState = useSelector(
    (state) => state.dashboard.overviewHistory,
  );
  const overviewHistory = (overviewHistoryState?.data?.data || [])
    .filter((item) => periodTypeByName[item.period_name] === activePeriodType)
    .slice()
    .sort(
      (a, b) => ordenarPeriodo(a.period_name) - ordenarPeriodo(b.period_name),
    );
  function ordenarPeriodo(periodo) {
    const meses = {
      Jan: 0,
      Fev: 1,
      Mar: 2,
      Abr: 3,
      Mai: 4,
      Jun: 5,
      Jul: 6,
      Ago: 7,
      Set: 8,
      Out: 9,
      Nov: 10,
      Dez: 11,
    };
    // start_date real do período (via state.dashboard.periods) é a fonte
    // confiável — o parser de "Mês/Ano" abaixo só entende abreviações de mês
    // e cairia tudo no mesmo bucket pra nomes trimestrais/semestrais/anuais
    // (ex. "T1/26"), embaralhando a ordem quando esses tipos são exibidos.
    const startDate = periodMetaByName[periodo]?.start_date;
    if (startDate) {
      const parsed = new Date(startDate).getTime();
      if (!Number.isNaN(parsed)) return parsed;
    }
    const [mes, ano] = String(periodo || "").split("/");
    return (2000 + Number(ano)) * 12 + (meses[mes] ?? 0);
  }

  return (() => {
    const trA = { label: AGRUP_LABELS[agrupT] || agrupT };

    const npsTrend = overviewHistory.map((o) => ({
      p: o.period_name,
      n: o.nps,
      s: o.nps_unimed_system,
    }));
    const cesTrend = overviewHistory.map((o) => ({
      p: o.period_name,
      v: o.ces,
      sys: o.ces_unimed_system,
    }));
    const csatTrend = overviewHistory.map((o) => ({
      p: o.period_name,
      v: o.csat,
      sys: o.csat_unimed_system,
    }));
    const jcxTrend = overviewHistory.map((o) => ({
      p: o.period_name,
      v: o.cx,
      sys: o.cx_unimed_system,
    }));

    const etapaHist = transformarMetricsEmEtapaHist(
      metricsHistoryData,
      Object.keys(JCFG),
      periodMetaById,
      ordenarPeriodo,
    );

    const npsValores = npsTrend
      .flatMap((x) => [x.n, x.s])
      .filter((valor) => Number.isFinite(Number(valor)))
      .map(Number);
    const npsMin = (npsValores.length ? Math.min(...npsValores) : 0) - 6;
    const npsMax = (npsValores.length ? Math.max(...npsValores) : 100) + 8;
    const AGRUP_OPTS = [
      { k: "mes", l: "Meses", sub: "12 meses" },
      { k: "tri", l: "Trimestres", sub: "5 trimestres" },
      { k: "sem", l: "Semestres", sub: "5 semestres" },
      { k: "ano", l: "Anos", sub: "4 anos" },
    ];

    // ─── REGRESSÃO LINEAR + PROJEÇÃO ────────────────────────────────────
    // Mínimos quadrados sobre os últimos N pontos da série, depois projeta
    // 6 meses e 12 meses adiante. Range é ± 1.5 × desvio padrão dos resíduos
    // (aproximação prática pra ~80% de intervalo de confiança).
    const calcTendencia = (serie, valorKey) => {
      const pontos = serie
        .map((p, i) => ({ x: i + 1, y: Number(p[valorKey]) }))
        .filter((p) => Number.isFinite(p.y));
      const N = pontos.length;
      if (N === 0) return null;
      // "Valor atual" só precisa do último ponto — não trava atrás do
      // mínimo de 3 pontos exigido pra regressão/projeção (ex.: poucos
      // trimestres/semestres/anos registrados ainda não devem zerar o
      // valor atual, só a tendência/projeção, que de fato precisam de N≥3).
      if (N < 3) {
        return {
          slope: null,
          valorAtual: Math.round(pontos[N - 1].y),
          proj6: null,
          proj12: null,
          tipo: "estavel",
          erroPadrao: null,
        };
      }
      const xBar = pontos.reduce((s, p) => s + p.x, 0) / N;
      const yBar = pontos.reduce((s, p) => s + p.y, 0) / N;
      const num = pontos.reduce((s, p) => s + (p.x - xBar) * (p.y - yBar), 0);
      const den = pontos.reduce((s, p) => s + Math.pow(p.x - xBar, 2), 0);
      const m = den === 0 ? 0 : num / den;
      const b = yBar - m * xBar;
      // Resíduos
      const residuos = pontos.map((p) => p.y - (m * p.x + b));
      const se = Math.sqrt(
        residuos.reduce((s, r) => s + r * r, 0) / Math.max(1, N - 2),
      );
      // Projeções (assumindo cada ponto = 1 mês)
      const valorAtual = pontos[N - 1].y;
      const proj6 = m * (N + 6) + b;
      const proj12 = m * (N + 12) + b;
      const range = Math.max(2, Math.round(1.5 * se));
      // Classificação da tendência (slope por mês)
      const tipo = Math.abs(m) < 0.3 ? "estavel" : m > 0 ? "alta" : "baixa";
      return {
        slope: Math.round(m * 100) / 100,
        valorAtual: Math.round(valorAtual),
        proj6: {
          centro: Math.round(proj6),
          min: Math.round(proj6 - range),
          max: Math.round(proj6 + range),
        },
        proj12: {
          centro: Math.round(proj12),
          min: Math.round(proj12 - range),
          max: Math.round(proj12 + range),
        },
        tipo,
        erroPadrao: Math.round(se * 10) / 10,
      };
    };

    const tendNps = calcTendencia(npsTrend, "n");
    const tendCes = calcTendencia(cesTrend, "v");
    const tendCsat = calcTendencia(csatTrend, "v");

    const isValorAusente = (valor) =>
      valor === null ||
      valor === undefined ||
      valor === "" ||
      (typeof valor === "number" && Number.isNaN(valor));

    const formatTrendValue = (
      valor,
      unidade = "",
      { prefix = "", withSign = false, suffix = "" } = {},
    ) => {
      if (isValorAusente(valor)) return "-";
      const sign = withSign && Number(valor) > 0 ? "+" : "";
      return `${prefix}${sign}${valor}${unidade}${suffix}`;
    };

    const TIPO_INFO = {
      alta: { label: "Tendência de alta", icon: ArrowUpRight, color: C.green },
      baixa: {
        label: "Tendência de baixa",
        icon: ArrowDownRight,
        color: C.red,
      },
      estavel: {
        label: "Tendência de estabilidade",
        icon: TrendingUp,
        color: C.orange,
      },
    };

    return (
      <div
        className="tabTrendsView"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: C.text,
                letterSpacing: "-0.02em",
              }}
            >
              Evolução histórica
            </div>
            <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>
              Agrupamento: <b style={{ color: C.text2 }}>{trA.label}</b>
            </div>
          </div>
          {/* Seletor de granularidade */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Calendar size={14} color={C.text3} />
            <div
              style={{
                fontSize: 10,
                color: C.text4,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginRight: 2,
              }}
            >
              Agrupar por
            </div>
            <div
              style={{
                display: "flex",
                background: C.surf3,
                borderRadius: 11,
                padding: 3,
                gap: 2,
                border: `1px solid ${C.border}`,
                boxShadow: "0 1px 3px rgba(0,0,0,.04)",
              }}
            >
              {AGRUP_OPTS.map((o) => {
                const active = agrupT === o.k;
                return (
                  <button
                    key={o.k}
                    onClick={() => setAgrupT(o.k)}
                    title={o.sub}
                    style={{
                      background: active ? C.g700 : "transparent",
                      color: active ? "#FFFFFF" : C.text2,
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 13px",
                      fontSize: 12,
                      fontWeight: active ? 700 : 500,
                      cursor: "pointer",
                      transition: "all .15s",
                      boxShadow: active
                        ? "0 2px 6px rgba(22,101,52,.25)"
                        : "none",
                      fontFamily: "inherit",
                    }}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* === TENDÊNCIA E PROJEÇÃO — 3 cards consolidados === */}
        <Section
          title="Tendência atual e projeção"
          // pad={false}
          action={
            <span style={{ fontSize: 10, color: C.text4, fontStyle: "italic" }}>
              Regressão linear sobre a série · ver cálculo na Metodologia
            </span>
          }
        >
          <div style={{ padding: "4px 20px 18px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 12,
              }}
            >
              {[
                {
                  nome: "NPS Geral",
                  tend: tendNps,
                  cor: C.g700,
                  unidade: "pts",
                },
                {
                  nome: "CES Consolidado",
                  tend: tendCes,
                  cor: C.blue,
                  unidade: "%",
                },
                {
                  nome: "CSAT Consolidado",
                  tend: tendCsat,
                  cor: C.g600,
                  unidade: "%",
                },
              ].map(({ nome, tend, cor, unidade }) => {
                const info = TIPO_INFO[tend?.tipo] || TIPO_INFO.estavel;
                const IconComp = info.icon;
                const tendenciaInsuficiente = !tend || tend.slope === null;
                const slopeAusente = isValorAusente(tend?.slope);
                return (
                  <Card
                    key={nome}
                    style={{
                      padding: "16px 18px",
                      borderTop: `4px solid ${cor}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: cor,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            marginBottom: 2,
                          }}
                        >
                          {nome}
                        </div>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: C.text,
                            letterSpacing: "-0.03em",
                            fontFamily: F.display,
                            lineHeight: 1,
                          }}
                        >
                          {formatTrendValue(tend?.valorAtual, unidade)}
                        </div>
                        <div
                          style={{ fontSize: 9, color: C.text4, marginTop: 2 }}
                        >
                          valor atual
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "5px 10px",
                          borderRadius: 20,
                          background: `${info.color}15`,
                          color: info.color,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        <IconComp size={12} />
                        {info.label}
                      </div>
                    </div>

                    {/* Slope explicativo */}
                    <div
                      style={{
                        fontSize: 10,
                        color: C.text4,
                        marginBottom: 10,
                        paddingBottom: 10,
                        borderBottom: `1px dashed ${C.border}`,
                      }}
                    >
                      {tendenciaInsuficiente ? (
                        <b style={{ color: C.text3 }}>
                          Necessário no mínimo 3{" "}
                          {AGRUP_PLURAL[agrupT] || "meses"} para calcular a
                          tendência
                        </b>
                      ) : (
                        <>
                          Variação média:{" "}
                          <b
                            style={{
                              color:
                                tend?.slope > 0
                                  ? C.green
                                  : tend?.slope < 0
                                    ? C.red
                                    : C.text3,
                            }}
                          >
                            {formatTrendValue(tend?.slope, unidade, {
                              withSign: true,
                              suffix: slopeAusente ? "" : "/período",
                            })}
                          </b>{" "}
                          · Erro padrão{" "}
                          {formatTrendValue(tend?.erroPadrao, unidade, {
                            prefix: isValorAusente(tend?.erroPadrao) ? "" : "±",
                          })}
                        </>
                      )}
                    </div>

                    {/* Projeção 6 meses */}
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          Projeção 6 meses
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: C.text,
                            fontFamily: F.display,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {formatTrendValue(tend?.proj6?.min, unidade)}
                        </span>
                        <span style={{ fontSize: 11, color: C.text4 }}>a</span>
                        <span
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: C.text,
                            fontFamily: F.display,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {formatTrendValue(tend?.proj6?.max, unidade)}
                        </span>
                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: 10,
                            color: C.text4,
                          }}
                        >
                          centro{" "}
                          {formatTrendValue(tend?.proj6?.centro, unidade)}
                        </span>
                      </div>
                    </div>

                    {/* Projeção 1 ano */}
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          Projeção 12 meses
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: C.text,
                            fontFamily: F.display,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {formatTrendValue(tend?.proj12?.min, unidade)}
                        </span>
                        <span style={{ fontSize: 11, color: C.text4 }}>a</span>
                        <span
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: C.text,
                            fontFamily: F.display,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {formatTrendValue(tend?.proj12?.max, unidade)}
                        </span>
                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: 10,
                            color: C.text4,
                          }}
                        >
                          centro{" "}
                          {formatTrendValue(tend?.proj12?.centro, unidade)}
                        </span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                background: C.surf3,
                borderRadius: 8,
                fontSize: 10,
                color: C.text4,
                lineHeight: 1.6,
              }}
            >
              <b style={{ color: C.text3 }}>Como ler:</b> projeções assumem que
              o ritmo de variação dos últimos meses se mantém, sem novos planos
              de ação ou eventos disruptivos. O range (mín–máx) reflete a
              incerteza estatística do modelo — quanto mais oscila a série,
              maior o range.{" "}
              <b>
                O cálculo completo está documentado na aba Metodologia &gt;
                Projeções.
              </b>
            </div>
          </div>
        </Section>

        {/* Jornada CX Consolidada */}
        <Section
          title={`Jornada CX (Índice) — Visão ${trA.label.toLowerCase()}`}
          action={
            <span style={{ fontSize: 10, color: C.text4, fontStyle: "italic" }}>
              Média consolidada de todas as 11 etapas da jornada
            </span>
          }
        >
          <div style={{ padding: "0 20px 18px" }}>
            <div
              style={{
                display: "flex",
                gap: 16,
                marginBottom: 12,
                paddingTop: 4,
              }}
            >
              {[
                ["Atual", jcxTrend[jcxTrend.length - 1]?.v + "%", "#d97706"],
                ["Meta", formatMeta(metas.jcx, "%"), C.p500],
                [
                  "Sist. Unimed",
                  jcxTrend[jcxTrend.length - 1]?.sys + "%",
                  C.text4,
                ],
              ].map(([l, v, c]) => (
                <div key={l}>
                  <div
                    style={{
                      fontSize: 9,
                      color: C.text4,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {l}
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: c,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={jcxTrend} margin={{ top: 12, right: 24 }}>
                <defs>
                  <linearGradient id="gJcxCons" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#d97706" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#d97706" stopOpacity={0} />
                  </linearGradient>
                </defs>
                {Zonas({ tipo: "ces" })}
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={C.border}
                  vertical={false}
                />
                <XAxis dataKey="p" tick={{ fontSize: 11, fill: C.text2 }} />
                <YAxis
                  domain={[
                    Math.max(0, Math.min(...jcxTrend.map((x) => x.v)) - 10),
                    Math.min(
                      100,
                      Math.max(...jcxTrend.map((x) => x.v), metas.jcx) + 10,
                    ),
                  ]}
                  tick={{ fontSize: 10, fill: C.text4 }}
                  tickFormatter={(v) => v + "%"}
                />
                <Tooltip content={<TT />} />
                {metas.jcx !== null && (
                  <ReferenceLine
                    y={metas.jcx}
                    stroke={C.p500}
                    strokeDasharray="5 3"
                    label={{
                      value: `Meta ${metas.jcx}%`,
                      position: "right",
                      fontSize: 10,
                      fill: C.p600,
                    }}
                  />
                )}
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  isAnimationActive={animarGraficos}
                  type="monotone"
                  dataKey="v"
                  name="Jornada CX"
                  stroke="#d97706"
                  strokeWidth={2.5}
                  fill="url(#gJcxCons)"
                  dot={{ fill: "#d97706", r: 4, strokeWidth: 0 }}
                />
                <Area
                  isAnimationActive={animarGraficos}
                  type="monotone"
                  dataKey="sys"
                  name="Média Sist. Unimed"
                  stroke={C.text4}
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                  fill="transparent"
                  dot={{ fill: C.text4, r: 3, strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section
          title={`NPS Geral — Visão ${trA.label.toLowerCase()}`}
        >
          <div style={{ padding: "0 20px 18px" }}>
            <div
              style={{
                display: "flex",
                gap: 16,
                marginBottom: 12,
                paddingTop: 4,
              }}
            >
              {[
                ["Atual", npsTrend[npsTrend.length - 1]?.n, C.g600],
                ["Meta", formatMeta(metas.nps), C.p500],
                [
                  "Sist. Unimed",
                  npsTrend[npsTrend.length - 1]?.s,
                  C.text4,
                ],
              ].map(([l, v, c]) => (
                <div key={l}>
                  <div
                    style={{
                      fontSize: 9,
                      color: C.text4,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {l}
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: c,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {v ?? "—"}
                  </div>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={npsTrend} margin={{ top: 12, right: 24 }}>
                <defs>
                  <linearGradient id="gnps" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={C.g600} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={C.g600} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gsys" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={C.g300} stopOpacity={0.1} />
                    <stop offset="95%" stopColor={C.g300} stopOpacity={0} />
                  </linearGradient>
                </defs>
                {Zonas({ tipo: "nps" })}
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={C.border}
                  vertical={false}
                />
                <XAxis dataKey="p" tick={{ fontSize: 11, fill: C.text2 }} />
                <YAxis
                  domain={[Math.max(0, npsMin), Math.min(100, npsMax)]}
                  tick={{ fontSize: 10, fill: C.text4 }}
                />
                <Tooltip content={<TT />} />
                {metas.nps !== null && (
                  <ReferenceLine
                    y={metas.nps}
                    stroke={C.p500}
                    strokeDasharray="5 3"
                    label={{
                      value: `Meta ${metas.nps}`,
                      position: "right",
                      fontSize: 10,
                      fill: C.p600,
                    }}
                  />
                )}
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  isAnimationActive={animarGraficos}
                  type="monotone"
                  dataKey="n"
                  name="NPS Unimed"
                  stroke={C.g600}
                  strokeWidth={2.5}
                  fill="url(#gnps)"
                  dot={{ fill: C.g600, r: 4, strokeWidth: 0 }}
                />
                <Area
                  isAnimationActive={animarGraficos}
                  type="monotone"
                  dataKey="s"
                  name="Média Sist. Unimed"
                  stroke={C.g300}
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                  fill="url(#gsys)"
                  dot={{ fill: C.g300, r: 3, strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Section>

        {/* CES + CSAT consolidados */}
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
        >
          <Section
            title="CES Consolidado — Médio das 5 etapas de esforço"
            // pad={false}
          >
            <div style={{ padding: "0 20px 18px" }}>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginBottom: 8,
                  paddingTop: 4,
                }}
              >
                {[
                  [
                    "Atual",
                    (cesTrend[cesTrend.length - 1]?.v ?? "-") + "%",
                    C.blue,
                  ],
                  ["Meta", formatMeta(metas.ces, "%"), C.p500],
                  ["Sist. Unimed", (cesTrend[0]?.sys ?? "-") + "%", C.text4],
                ].map(([l, v, c]) => (
                  <div key={l}>
                    <div
                      style={{
                        fontSize: 9,
                        color: C.text4,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {l}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: c,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {v}
                    </div>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart
                  data={cesTrend}
                  margin={{ top: 4, right: 20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="gCesCons" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={C.blue} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={C.blue} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <ReferenceArea
                    y1={0}
                    y2={40}
                    fill="#99111108"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={40}
                    y2={50}
                    fill="#dc262608"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={50}
                    y2={60}
                    fill="#d9770608"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={60}
                    y2={70}
                    fill="#0284c708"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={70}
                    y2={80}
                    fill="#16a34a08"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={80}
                    y2={100}
                    fill="#05966908"
                    strokeWidth={0}
                  />
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={C.border}
                    vertical={false}
                  />
                  <XAxis dataKey="p" tick={{ fontSize: 11, fill: C.text2 }} />
                  <YAxis
                    domain={[
                      Math.max(0, Math.min(...cesTrend.map((x) => x.v)) - 8),
                      Math.min(
                        100,
                        Math.max(...cesTrend.map((x) => x.v), metas.ces) + 8,
                      ),
                    ]}
                    tick={{ fontSize: 10, fill: C.text4 }}
                    tickFormatter={(v) => v + "%"}
                  />
                  <Tooltip content={<TT />} />
                  {metas.ces !== null && (
                    <ReferenceLine
                      y={metas.ces}
                      stroke={C.p500}
                      strokeDasharray="5 3"
                      label={{
                        value: `Meta ${metas.ces}%`,
                        position: "right",
                        fontSize: 9,
                        fill: C.p600,
                      }}
                    />
                  )}
                  <ReferenceLine
                    y={cesTrend[0]?.sys}
                    stroke={C.g300}
                    strokeDasharray="4 2"
                    label={{
                      value: `Sist. Unimed ${cesTrend[0]?.sys}%`,
                      position: "right",
                      fontSize: 9,
                      fill: C.g400,
                    }}
                  />
                  <Area
                    isAnimationActive={animarGraficos}
                    type="monotone"
                    dataKey="v"
                    name="CES médio"
                    stroke={C.blue}
                    strokeWidth={2.5}
                    fill="url(#gCesCons)"
                    dot={{ fill: C.blue, r: 4, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <Section
            title="CSAT Consolidado — Médio das 6 etapas de satisfação"
            // pad={false}
          >
            <div style={{ padding: "0 20px 18px" }}>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginBottom: 8,
                  paddingTop: 4,
                }}
              >
                {[
                  [
                    "Atual",
                    (csatTrend[csatTrend.length - 1]?.v ?? "-") + "%",
                    C.g600,
                  ],
                  ["Meta", formatMeta(metas.csat, "%"), C.p500],
                  ["Sist. Unimed", (csatTrend[0]?.sys ?? "-") + "%", C.text4],
                ].map(([l, v, c]) => (
                  <div key={l}>
                    <div
                      style={{
                        fontSize: 9,
                        color: C.text4,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {l}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: c,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {v}
                    </div>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart
                  data={csatTrend}
                  margin={{ top: 4, right: 20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="gCsatCons" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={C.g600} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={C.g600} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <ReferenceArea
                    y1={0}
                    y2={40}
                    fill="#99111108"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={40}
                    y2={50}
                    fill="#dc262608"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={50}
                    y2={60}
                    fill="#d9770608"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={60}
                    y2={70}
                    fill="#0284c708"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={70}
                    y2={80}
                    fill="#16a34a08"
                    strokeWidth={0}
                  />
                  <ReferenceArea
                    y1={80}
                    y2={100}
                    fill="#05966908"
                    strokeWidth={0}
                  />
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={C.border}
                    vertical={false}
                  />
                  <XAxis dataKey="p" tick={{ fontSize: 11, fill: C.text2 }} />
                  <YAxis
                    domain={[
                      Math.max(0, Math.min(...csatTrend.map((x) => x.v)) - 8),
                      Math.min(
                        100,
                        Math.max(...csatTrend.map((x) => x.v), metas.csat) + 8,
                      ),
                    ]}
                    tick={{ fontSize: 10, fill: C.text4 }}
                    tickFormatter={(v) => v + "%"}
                  />
                  <Tooltip content={<TT />} />
                  {metas.csat !== null && (
                    <ReferenceLine
                      y={metas.csat}
                      stroke={C.p500}
                      strokeDasharray="5 3"
                      label={{
                        value: `Meta ${metas.csat}%`,
                        position: "right",
                        fontSize: 9,
                        fill: C.p600,
                      }}
                    />
                  )}
                  <ReferenceLine
                    y={csatTrend[0]?.sys}
                    stroke={C.g300}
                    strokeDasharray="4 2"
                    label={{
                      value: `Sist. Unimed ${csatTrend[0]?.sys}%`,
                      position: "right",
                      fontSize: 9,
                      fill: C.g400,
                    }}
                  />
                  <Area
                    isAnimationActive={animarGraficos}
                    type="monotone"
                    dataKey="v"
                    name="CSAT% médio"
                    stroke={C.g600}
                    strokeWidth={2.5}
                    fill="url(#gCsatCons)"
                    dot={{ fill: C.g600, r: 4, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Section>
        </div>

        <div
          style={{
            fontSize: 10,
            color: C.text4,
            fontStyle: "italic",
            marginTop: -4,
            marginBottom: -2,
          }}
        >
          Sparklines abaixo mostram sempre a granularidade mensal por etapa,
          independente do agrupamento selecionado acima.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 12,
          }}
        >
          {(() => {
            return Object.entries(etapaHist)
              .sort(([a], [b]) => compareJourneyStages(a, b))
              .map(([nome, hist]) => {
                const cfg = JCFG[nome];
                if (!cfg) return null;
                const isCes = cfg.tipo === "ces";
                const meta = Number.isFinite(metaByEtapa[nome])
                  ? metaByEtapa[nome]
                  : null;
                const latest = hist[hist.length - 1] && hist[hist.length - 1].v;
                const first = hist[0] && hist[0].v;
                const latestSystem = hist[hist.length - 1]?.s;
                const st = etSt(nome, latest);
                const IcoT = JICONS[nome];
                const vals = hist.map((h) => h.v);
                const valsComMeta = meta !== null ? [...vals, meta] : vals;
                const yMin = Math.max(0, Math.min(...valsComMeta) - 8);
                const yMax = Math.min(100, Math.max(...valsComMeta) + 8);

                return (
                  <Card key={nome} style={{ padding: "14px 16px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            background: isCes ? C.blueBg : C.greenBg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {IcoT && (
                            <IcoT
                              size={13}
                              color={isCes ? C.blue : C.g700}
                              strokeWidth={2}
                            />
                          )}
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: C.text,
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {nome}
                          </div>
                          <Tag
                            label={isCes ? "CES" : "CSAT"}
                            color={isCes ? C.blue : C.g700}
                          />
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: st.color,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {latest}%
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color:
                              latest - first > 0
                                ? C.green
                                : latest - first < 0
                                  ? C.red
                                  : C.text4,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          {latest - first > 0 && <ArrowUpRight size={10} />}
                          {latest - first < 0 && <ArrowDownRight size={10} />}
                          {Math.abs(latest - first)}pp
                        </div>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={72}>
                      <LineChart
                        data={hist}
                        margin={{ top: 4, bottom: 0, left: 0, right: 4 }}
                      >
                        {Z_CX.map(([y1, y2, fill], i) => (
                          <ReferenceArea
                            key={i}
                            y1={y1}
                            y2={y2}
                            fill={fill}
                            strokeWidth={0}
                          />
                        ))}
                        <XAxis
                          dataKey="p"
                          tick={{ fontSize: 9, fill: C.text4 }}
                          height={14}
                        />
                        <YAxis domain={[yMin, yMax]} hide />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !(payload && payload.length))
                              return null;
                            const vPoint = payload.find(
                              (p) => p.dataKey === "v",
                            );
                            const sPoint = payload.find(
                              (p) => p.dataKey === "s",
                            );
                            return (
                              <div
                                style={{
                                  background: C.g900,
                                  borderRadius: 7,
                                  padding: "5px 9px",
                                  fontSize: 11,
                                }}
                              >
                                <div style={{ color: C.g300, marginBottom: 2 }}>
                                  {label}
                                </div>
                                <div style={{ color: "#fff", fontWeight: 700 }}>
                                  {vPoint ? `${vPoint.value}%` : "-"}
                                </div>
                                <div
                                  style={{
                                    color: "rgba(255,255,255,.65)",
                                    fontSize: 9,
                                  }}
                                >
                                  Sist. Unimed:{" "}
                                  {sPoint && sPoint.value !== undefined
                                    ? `${sPoint.value}%`
                                    : "-"}
                                </div>
                                <div
                                  style={{
                                    color: "rgba(255,255,255,.45)",
                                    fontSize: 9,
                                  }}
                                >
                                  Meta: {meta !== null ? `${meta}%` : "-"}
                                </div>
                              </div>
                            );
                          }}
                        />
                        {meta !== null && (
                          <ReferenceLine
                            y={meta}
                            stroke={C.p500}
                            strokeDasharray="3 2"
                            strokeWidth={1}
                          />
                        )}
                        <Line
                          isAnimationActive={animarGraficos}
                          type="monotone"
                          dataKey="s"
                          name="Sist. Unimed"
                          stroke={C.text4}
                          strokeWidth={1.5}
                          strokeDasharray="4 2"
                          dot={{ fill: C.text4, r: 2, strokeWidth: 0 }}
                          connectNulls
                        />
                        <Line
                          isAnimationActive={animarGraficos}
                          type="monotone"
                          dataKey="v"
                          stroke={st.color}
                          strokeWidth={2}
                          dot={{ fill: st.color, r: 2.5, strokeWidth: 0 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 10,
                        color: C.text4,
                        marginTop: 4,
                      }}
                    >
                      <span>Meta: {meta !== null ? `${meta}%` : "-"}</span>
                      <span>
                        Sist. Unimed:{" "}
                        {latestSystem !== undefined ? `${latestSystem}%` : "-"}
                      </span>
                      <Tag label={st.label} color={st.color} bg={st.bg} />
                    </div>
                  </Card>
                );
              });
          })()}
        </div>

        {/* === SÉRIE HISTÓRICA POR CIDADE === */}
        {(() => {
          const dataChart = transformarNpsPorCidadeParaGrafico(
            segmentationData,
            ordenarPeriodo,
          );
          const cidadesChart = cidadesDaSerie(dataChart);
          const metaNpsCidade = metas.nps;
          const dominioCidade = dominioNpsPorCidade(dataChart, metaNpsCidade);
          const resumoCidade = resumoNpsPorCidade(dataChart, {
            singular: AGRUP_SINGULAR[agrupT],
            plural: AGRUP_PLURAL[agrupT],
          });

          // Cores por cidade — paleta consistente com o design system
          const CORES_CIDADE = [
            C.g700,
            C.blue,
            C.purple || "#7C68EE",
            C.orange,
            "#06b6d4",
            "#ec4899",
            "#84cc16",
            "#f43f5e",
          ];

          return (
            <Section
              title="Série histórica do NPS por cidade"
              // pad={false}
              action={
                <span
                  style={{ fontSize: 10, color: C.text4, fontStyle: "italic" }}
                >
                  Permite identificar cidades em queda e priorizar planos de
                  ação localizados
                </span>
              }
            >
              <div style={{ padding: "4px 20px 18px" }}>
                <div
                  style={{
                    fontSize: 11,
                    color: C.text3,
                    marginBottom: 10,
                    lineHeight: 1.5,
                  }}
                >
                  {resumoCidade}
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={dataChart}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 11, fill: C.text3 }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: C.text4 }}
                      domain={dominioCidade}
                    />
                    <Tooltip content={<TT />} />
                    {cidadesChart.map((c, i) => (
                      <Line
                        isAnimationActive={animarGraficos}
                        key={c}
                        type="monotone"
                        dataKey={c}
                        stroke={CORES_CIDADE[i % CORES_CIDADE.length]}
                        strokeWidth={2}
                        // Cidade abaixo do piso de respostas num mês não tem
                        // ponto nele; sem isto a linha se partia e sobravam
                        // pontos soltos (ex.: Ago/26 com só 4 cidades).
                        connectNulls
                        dot={{
                          r: 3,
                          fill: CORES_CIDADE[i % CORES_CIDADE.length],
                          strokeWidth: 0,
                        }}
                        activeDot={{ r: 5 }}
                      />
                    ))}
                    {metas.nps !== null && (
                      <ReferenceLine
                        y={metas.nps}
                        stroke={C.p500}
                        strokeDasharray="3 2"
                        strokeWidth={1}
                        label={{
                          value: `Meta ${metas.nps}`,
                          position: "right",
                          fill: C.p500,
                          fontSize: 10,
                        }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
                {/* Legenda customizada */}
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    flexWrap: "wrap",
                    marginTop: 12,
                    padding: "10px 14px",
                    background: C.surf3,
                    borderRadius: 9,
                  }}
                >
                  {cidadesChart.map((c, i) => {
                    const cor = CORES_CIDADE[i % CORES_CIDADE.length];
                    const valoresCidade = dataChart
                      .map((row) => row[c])
                      .filter((valor) => Number.isFinite(Number(valor)))
                      .map(Number);
                    const valorAtual = valoresCidade[valoresCidade.length - 1];
                    const valorAnt = valoresCidade[0];
                    const delta = valorAtual - valorAnt;
                    return (
                      <div
                        key={c}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            background: cor,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            color: C.text2,
                            fontWeight: 600,
                          }}
                        >
                          {c}
                        </span>
                        <span style={{ fontSize: 11, color: C.text4 }}>
                          {valorAtual}
                        </span>
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color:
                              delta > 0
                                ? C.green
                                : delta < 0
                                  ? C.red
                                  : C.text4,
                          }}
                        >
                          {delta > 0 ? "↗ +" : delta < 0 ? "↘ " : ""}
                          {delta}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Section>
          );
        })()}
      </div>
    );
  })();
}
